import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import API_BASE_URL from "../../config/api";
import { usePageStyles } from "../common/pageStyles";

const BASE_URL = API_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  // Token czytany przy każdym żądaniu, a nie raz przy ładowaniu skryptu
  // (inaczej po zalogowaniu bez odświeżenia strony wysyłany byłby stary/pusty token).
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Konfiguracja obu zestawow danych obslugiwanych przez ten komponent. Uzytkownik
// przelacza sie miedzy nimi u gory - filtrowanie, dodawanie i usuwanie dziala na
// aktualnie wybranym zestawie.
const DATASETS = {
  firmy: {
    label: "Firmy (nadawcy)",
    heading: "Lista firm nadawców dla transportów Magazyn Warszawa",
    listUrl: `${BASE_URL}/list/listMagazynWarszawa`,
    addUrl: `${BASE_URL}/add-record/listMagazynWarszawa`,
    deleteUrl: (id) => `${BASE_URL}/delete/listMagazynWarszawa/${id}`,
    columnLabel: "Nazwa firmy",
    searchLabel: "Szukaj firmy…",
    addButtonLabel: "Dodaj firmę",
    inputLabel: "Nazwa firmy",
    emptyLabel: "Brak firm do wyświetlenia",
    emptyValueMsg: "Nazwa firmy nie może być pusta!",
    duplicateMsg: "Firma o takiej nazwie już istnieje w liście!",
    // Firmy zapisujemy wielkimi literami (spojne z reszta aplikacji).
    transform: (name) => name.trimStart().toUpperCase(),
  },
  adresaci: {
    label: "Adresaci problemów z przesyłką",
    heading: "Lista adresatów powiadomień o problemie z przesyłką",
    listUrl: `${BASE_URL}/list/adresaciProblemPrzesylki`,
    addUrl: `${BASE_URL}/add-record/adresaciProblemPrzesylki`,
    deleteUrl: (id) => `${BASE_URL}/delete/adresaciProblemPrzesylki/${id}`,
    columnLabel: "Adresat",
    searchLabel: "Szukaj adresata…",
    addButtonLabel: "Dodaj adresata",
    inputLabel: "Adresat (e-mail lub imię i nazwisko)",
    emptyLabel: "Brak adresatów do wyświetlenia",
    emptyValueMsg: "Adresat nie może być pusty!",
    duplicateMsg: "Taki adresat już istnieje w liście!",
    // Adresatow zapisujemy w pisowni podanej przez uzytkownika.
    transform: (name) => name.trim(),
  },
};

const MagazynWarszawaLists = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState("firmy");
  const cfg = DATASETS[dataset];

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newRecordName, setNewRecordName] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const handleDatasetChange = (key) => {
    setDataset(key);
    setDeleteTarget(null);
    setSearchTerm("");
    setNewRecordName("");
    setAddOpen(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(cfg.listUrl, { headers: getHeaders() });
      setTableData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Wystąpił błąd podczas pobierania danych.", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [cfg.listUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(cfg.deleteUrl(deleteTarget.id), { headers: getHeaders() });
      await fetchData();
      toast.success(`Poprawnie usunięto ${deleteTarget.name}!`, { autoClose: 3000 });
      setDeleteTarget(null);
    } catch (error) {
      console.error("Error deleting row:", error);
      toast.error("Wystąpił błąd podczas usuwania.", { autoClose: 3000 });
    }
  };

  // Duplikaty sprawdzamy bez wzgledu na wielkosc liter.
  const isDuplicate = (name) => {
    const normalized = name.trim().toLowerCase();
    return tableData.some((row) => String(row.name).toLowerCase() === normalized);
  };

  const handleSaveRecord = async () => {
    if (!newRecordName.trim()) {
      toast.error(cfg.emptyValueMsg, { autoClose: 3000 });
      return;
    }
    if (isDuplicate(newRecordName)) {
      toast.error(cfg.duplicateMsg, { autoClose: 3000 });
      return;
    }
    try {
      await axios.post(cfg.addUrl, { name: cfg.transform(newRecordName) }, { headers: getHeaders() });
      await fetchData();
      setNewRecordName("");
      setAddOpen(false);
      toast.success("Poprawnie dodano nowy rekord!", { autoClose: 3000 });
    } catch (error) {
      console.error("Error adding new record:", error);
      toast.error("Wystąpił błąd podczas dodawania.", { autoClose: 3000 });
    }
  };

  const handleBack = () => {
    // Ekran, z ktorego wchodzimy, zapisuje sciezke powrotu; domyslnie /user.
    const returnPath = localStorage.getItem("magazynWarszawaListsReturnPath") || "/user";
    localStorage.removeItem("magazynWarszawaListsReturnPath");
    navigate(returnPath);
  };

  const filteredTableData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return tableData.filter((row) => String(row.name).toLowerCase().includes(term));
  }, [tableData, searchTerm]);

  return (
    <>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>Zarządzanie listą Magazyn Warszawa</h1>
          <div className={classes.subtitle}>{cfg.heading}</div>
        </div>
        <div className={classes.headerActions}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Powrót
          </Button>
          <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            {cfg.addButtonLabel}
          </Button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.segmented} role="tablist" aria-label="Wybierz listę do zarządzania">
            {Object.entries(DATASETS).map(([key, item]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={dataset === key}
                className={`${classes.segment} ${dataset === key ? classes.segmentActive : ""}`}
                onClick={() => handleDatasetChange(key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              className={classes.searchInput}
              placeholder={cfg.searchLabel}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputProps={{ "aria-label": cfg.searchLabel }}
            />
            {searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm("")} aria-label="Wyczyść wyszukiwanie">
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          <span className={classes.toolbarGap} />
          <span style={{ color: "#666", fontSize: 14 }}>
            Pozycji: <strong style={{ color: "#212121" }}>{filteredTableData.length}</strong>
          </span>
        </div>

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} style={{ width: 90 }}>
                  Nr
                </TableCell>
                <TableCell className={classes.headCell}>{cfg.columnLabel}</TableCell>
                <TableCell className={classes.headCell} style={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTableData.map((row) => (
                <TableRow key={row.id} hover={false} className={classes.row}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <div className={classes.actions}>
                      <Tooltip title="Usuń z listy">
                        <IconButton size="small" onClick={() => setDeleteTarget(row)} aria-label={`Usuń ${row.name}`}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!loading && filteredTableData.length === 0 && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>{cfg.emptyLabel}</strong>
            </div>
          )}
        </div>
      </div>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{cfg.addButtonLabel}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={cfg.inputLabel}
            fullWidth
            value={newRecordName}
            onChange={(e) => setNewRecordName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSaveRecord()}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewRecordName("");
              setAddOpen(false);
            }}
          >
            Anuluj
          </Button>
          <Button onClick={handleSaveRecord} variant="contained" disableElevation>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Usunąć z listy?</DialogTitle>
        <DialogContent>
          Pozycja <strong>{deleteTarget?.name}</strong> zostanie usunięta z listy.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MagazynWarszawaLists;
